import React, { useMemo, useState } from 'react';
import { Box, SxProps, Stack, TablePagination, Paper } from '@mui/material';
import { Button } from 'system/atoms';
import {
  Candidate,
  CandidateBatchStatus,
  CandidateEmailCorrespondence,
  CandidateWithCandidateBatch
} from 'services/candidate/types';
import {
  getCandidateWithCandidateBatch,
  getCandidateEmailCorrespondencesByCandidate
} from 'services/candidate/api';
import { useQuery, useSubscriber } from 'services/api/hooks';
import { TabContent } from 'components/Tabs';
import { CvViewer } from 'components/CvViewer';
import { onUpdateCandidate } from 'services/candidate/subscriptions';
import { useDrawer } from 'services/drawer/hooks';
import { UpdateCandidateStatus } from 'services/candidate/components/UpdateCandidateStatus';
import { CandidateNotesTab } from 'services/candidate/components/CandidateReportDrawer/CandidateNotesTab';
import { AnalysisErrorNotice } from 'services/candidate/components/AnalysisError';
import { OverallScoreBar } from 'services/candidate/components/CandidateReportDrawer/OverallScoreBar';
import { RequirementOutcomesList } from 'services/candidate/components/CandidateReportDrawer/RequirementOutcomesList';
import { TabsValue } from 'services/candidate/components/CandidateReportDrawer/constants';
import { getCandidateDrawerTabs } from 'services/candidate/components/CandidateReportDrawer/utils/getCandidateDrawerTabs';
import { EmailCorrespondencesTab } from 'services/candidate/components/CandidateReportDrawer/EmailCorrespondencesTab';
import { Loading } from 'components/Loading';
import { CandidateHeading } from 'services/candidate/components/CandidateReportDrawer/CandidateHeading';
import {
  useGetRequirementOutcomesByCandidateQuery,
  useUpdateCandidateMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { CandidateStatus } from 'generated-graphql/types';
import { ConversationDetails } from 'modules/conversations/components/ConversationDetails';
import { ApplicationForm } from 'services/candidate/components/CandidateReportDrawer/ApplicationForm';
import { Lock } from '@mui/icons-material';
import { useConfirmationModal } from 'services/modals/hooks';
import { useConversationDetailsTab } from 'modules/conversations/atoms/tabsAtom';
import { ConversationDetailsTabs } from 'modules/conversations/constants/conversationDetailsTabs';
import { TabV2ButtonItem, TabsV2 } from 'common/ui/TabsV2';
import { AtsCandidateLink } from 'services/integration/components/AtsCandidateLink';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import { getFullName } from 'utils/fomatting';

const container: SxProps = {
  gap: '20px',
  mx: '20px',
  width: '1100px',
  height: '100%',
  position: 'relative'
};

const viewerContainer: SxProps = {
  height: '650px',
  overflow: 'auto',
  width: '600px',
  p: 1,
  borderStyle: 'solid',
  borderColor: 'customGrey.main',
  borderWidth: '1px'
};

type Props = {
  candidateId: string;
  candidateIds: string[] | null;
  candidateTableIndex: number | null;
  defaultCampaignTab?: boolean;
};

export const CandidateReportDrawer: React.FC<Props> = ({
  candidateId,
  candidateIds,
  candidateTableIndex,
  defaultCampaignTab = false
}) => {
  const defaultLockStatus = useFlag(Flags.DEFAULT_LOCK_UPDATE_STATUS);
  const [updateCandidate] = useUpdateCandidateMutation();
  const { showModal } = useConfirmationModal();
  const [selectedTab, setSelectedTab] = useState(
    defaultCampaignTab ? TabsValue.Campaign : TabsValue.Analysis
  );
  const { setSelectedTab: setConversationSelectedTab } =
    useConversationDetailsTab({
      onTabChange: (tab) => {
        if (tab === ConversationDetailsTabs.MeetingNotes) {
          setSelectedTab(TabsValue.MeetingNotes);
        }
      }
    });
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [emailCorrespondences, setEmailCorrespondences] = useState<
    CandidateEmailCorrespondence[] | undefined | null
  >(null);

  const { updateDrawerComponentProps } = useDrawer();

  const [candidateLocked, setCandidateLocked] = useState(false);
  const [candidateStatus, setCandidateStatus] =
    useState<CandidateStatus | null>(null);

  const { data: candidate, refetch: refetchCandidate } =
    useQuery<CandidateWithCandidateBatch>(
      getCandidateWithCandidateBatch,
      {
        id: candidateId
      },
      {
        onFetchSuccess: (data) => {
          setCandidateStatus(data?.status as any as CandidateStatus);
          setCandidateLocked(data?.isLocked || false);
          if (selectedTab === TabsValue.Campaign && !data.conversationId) {
            setSelectedTab(TabsValue.Analysis);
          }
        }
      }
    );

  const isReadOnly =
    candidate?.candidateBatch?.status === CandidateBatchStatus.ARCHIVED ||
    !!candidate?.candidateBatch?.isWorkflowManaged;

  const {
    data: requirementOutcomesData,
    loading: requirementsLoading,
    refetch: refetchRequirementOutcomes
  } = useGetRequirementOutcomesByCandidateQuery({
    variables: {
      candidateId
    },
    fetchPolicy: 'no-cache'
  });

  const requirementOutcomes = extractQueryListItems(requirementOutcomesData);

  useQuery<CandidateEmailCorrespondence[]>(
    getCandidateEmailCorrespondencesByCandidate,
    {
      candidateId: candidateId
    },
    {
      onFetchSuccess: (data) => {
        setEmailCorrespondences(data);
      }
    }
  );

  const fullName = useMemo(() => {
    if (!candidate) {
      return 'Loading...';
    }

    return getFullName(candidate.firstName, candidate.lastName);
  }, [candidate]);

  const tabs = getCandidateDrawerTabs({
    emailCorrespondences,
    conversation: candidate?.conversation
  });

  useSubscriber({
    subQuery: onUpdateCandidate,
    subParams: {
      id: { eq: candidateId }
    },
    onFetchSuccess: (data) => {
      const candidateUpdated: Candidate = data?.onUpdateCandidate;

      setCandidateLocked(candidateUpdated?.isLocked || false);

      if (candidateUpdated?.status) {
        setCandidateStatus(candidateUpdated.status as any as CandidateStatus);
      }

      // The analysis result can change while the drawer is open. Refetching
      // must not turn a transient network failure into an unhandled rejection.
      void Promise.allSettled([
        refetchCandidate(),
        refetchRequirementOutcomes()
      ]);
    }
  });

  const changePage = (page: number) => {
    if (candidateTableIndex === null || !candidateIds) return;
    const candidateFoundId = candidateIds.find((id, index) => index === page);
    if (!candidateFoundId) return;
    updateDrawerComponentProps({
      candidateTableIndex: page,
      candidateId: candidateFoundId,
      candidateIds
    });
  };

  if (!candidate || requirementsLoading) {
    return (
      <Box
        sx={{
          ...container,
          height: '100%'
        }}
      >
        <Loading size={18} />
      </Box>
    );
  }

  if (showUpdateStatus && !isReadOnly) {
    return (
      <Stack sx={container}>
        <CandidateHeading
          fullName={fullName}
          candidateStatus={candidateStatus}
          email={candidate.email}
          phoneNumber={candidate.phoneNumber}
        />
        <Box maxWidth={300}>
          <UpdateCandidateStatus
            defaultLockStatus={defaultLockStatus ?? false}
            candidateIds={[candidateId]}
            atsJobId={candidate.candidateBatch.atsJobId}
            defaultStatus={(candidateStatus as any) ?? undefined}
            onCancel={() => setShowUpdateStatus(false)}
            onClearSelectedCandidates={() => {
              setShowUpdateStatus(false);
            }}
          />
        </Box>
      </Stack>
    );
  }

  const handleOnStatusClick = () => {
    if (candidateLocked) {
      showModal({
        title: 'Unlock candidate?',
        message: 'This will allow you to change the candidate’s status.',
        confirmationText: 'Confirm',
        cancelText: 'Cancel',
        onConfirm: async () => {
          await updateCandidate({
            variables: {
              input: {
                id: candidate.id,
                isLocked: false
              }
            }
          });
          setShowUpdateStatus(true);
        }
      });
      return;
    }

    setShowUpdateStatus(true);
  };

  const onTabSelect = (value: TabsValue) => {
    setSelectedTab(value);

    if (value === TabsValue.MeetingNotes) {
      setConversationSelectedTab(ConversationDetailsTabs.MeetingNotes);
    }

    if (value === TabsValue.Campaign) {
      setConversationSelectedTab(ConversationDetailsTabs.ConversationDetails);
    }
  };

  const endItems: TabV2ButtonItem[] = [];

  if (!isReadOnly) {
    endItems.push(
      <Button
        onClick={handleOnStatusClick}
        variant="tertiary"
        startIcon={
          candidateLocked ? (
            <Lock color="primary" fontSize="small" />
          ) : undefined
        }
      >
        Status
      </Button>
    );
  }

  if (candidate.atsCandidateId) {
    endItems.push(
      <AtsCandidateLink
        atsCandidateId={candidate.atsCandidateId}
        atsApplicationId={candidate.atsApplicationId}
      />
    );
  }

  if (candidateIds) {
    endItems.push(
      <TablePagination
        sx={{
          mb: 0,
          pb: 0
        }}
        component="div"
        labelDisplayedRows={({ from, count }) => {
          return `${from} of ${count}`;
        }}
        count={candidateIds.length}
        page={candidateTableIndex ?? 0}
        rowsPerPage={1}
        rowsPerPageOptions={[]}
        onPageChange={(event, page) => {
          changePage(page);
        }}
      />
    );
  }

  return (
    <Stack sx={container}>
      <CandidateHeading
        fullName={fullName}
        candidateStatus={candidateStatus}
        email={candidate.email}
        phoneNumber={candidate.phoneNumber}
      />
      <TabsV2
        tabs={tabs}
        value={selectedTab}
        onChange={(value) => {
          onTabSelect(value as TabsValue);
        }}
        endItems={endItems}
      />
      <Box
        sx={{
          height: '100%'
        }}
      >
        <TabContent tab={TabsValue.Analysis} selectedTab={selectedTab}>
          <Stack direction="row" spacing="50px">
            <Box
              sx={{
                width: '450px',
                pb: '40px'
              }}
            >
              {candidate.analysisErrorCode ? (
                <AnalysisErrorNotice code={candidate.analysisErrorCode} />
              ) : (
                <>
                  <OverallScoreBar
                    totalScoreScaled={candidate.totalScoreScaled}
                  />

                  <RequirementOutcomesList
                    requirementOutcomes={requirementOutcomes ?? []}
                  />
                </>
              )}
            </Box>
            <Stack flex="1">
              {candidate.cvS3Key && (
                <Stack mb={2}>
                  <Paper sx={viewerContainer} elevation={1}>
                    <CvViewer
                      cvS3Key={candidate.cvS3Key}
                      cvParsedText={candidate.cvParsedText}
                    />
                  </Paper>
                </Stack>
              )}
              {!!candidate.atsQuestionnaireAnswers?.length && (
                <Stack width="100%">
                  <ApplicationForm
                    atsQuestionnaireAnswers={
                      candidate.atsQuestionnaireAnswers || []
                    }
                    createdAt={candidate.createdAt}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        </TabContent>

        {candidate.conversationId && (
          <TabContent tab={TabsValue.Campaign} selectedTab={selectedTab}>
            <ConversationDetails
              conversationId={candidate.conversationId}
              shouldHideCandidateDetails={true}
            />
          </TabContent>
        )}

        {candidate.conversationId &&
          candidate.conversation?.calendarBookingId && (
            <TabContent tab={TabsValue.MeetingNotes} selectedTab={selectedTab}>
              <ConversationDetails
                conversationId={candidate.conversationId}
                shouldHideCandidateDetails={true}
              />
            </TabContent>
          )}

        <TabContent tab={TabsValue.Notes} selectedTab={selectedTab}>
          <CandidateNotesTab candidateId={candidateId} readOnly={isReadOnly} />
        </TabContent>

        <TabContent tab={TabsValue.Email} selectedTab={selectedTab}>
          <EmailCorrespondencesTab
            emailCorrespondences={emailCorrespondences}
            setEmailCorrespondences={setEmailCorrespondences}
            readOnly={isReadOnly}
          />
        </TabContent>
      </Box>
    </Stack>
  );
};
